<template>
  <div id="challenge" v-if="challenge">
    <div class="full-overlay">
      <div class="overlay-header">
        <b-container fluid>
          <b-row align-h="center" align-v="center" class="my-md-4">
            <b-col>
            </b-col>
            <b-col>
              <b-progress v-if="challengeStarted && !challengeEnded" class="challenge-progress" height="0.5rem" :value="challengeProgress" :max="challengeLength" ></b-progress>
            </b-col>
            <b-col class="text-right">
              <b-button variant="link" id="close-button" :to="{ name: 'ChallengeTheme', params: { themeId: $parent.theme.id }}"><icon name="times"  scale="2"/></b-button>
            </b-col>
          </b-row>
        </b-container>
      </div>
      <div class="overlay-content">
        <b-container class="challenge-page h-100" fluid v-if="!challengeStarted && !challengeEnded">
          <b-row align-h="center" align-v="center" class="h-100">
            <b-col cols="12" md="10" lg="8" xl="8" v-if="previouslyChallengePageIndex === null || previouslyChallengeFinished">
              <h2 class="title">{{ challenge.title }}</h2>
              <p class="content">{{ challenge.description }}</p>
            </b-col>
            <b-col cols="12" md="10" lg="8" xl="8" v-else class="text-center">
              <h2 class="title">{{ $t('challenges:continue-progress-title') }}</h2>
              <div class="mb-4">
                <b-button variant="success" size="lg" @click="continueChallenge">{{ $t('challenges:continue-cta') }}</b-button>
              </div>
              <b-button variant="outline-primary" @click="startOver">{{ $t('challenges:start-over-cta') }}</b-button>
            </b-col>
          </b-row>
        </b-container>
        <b-container class="challenge-page h-100" fluid v-if="challengeStarted && !challengeEnded">
          <b-row align-h="center" align-v="center" class="h-100">
            <b-col cols="12" md="10" lg="8" xl="8">
              <h2 class="title">{{ challenge.pages[challengePage].title }}</h2>
              <p class="content">{{ challenge.pages[challengePage].content }}</p>
              <div class="challenge-page-journal-entries" v-if="challengePageJournalEntries && account.accountSetup && account.accountSetup.capabilities.journal === true">
                <CreateJournalEntry ref="from" :themeId="$parent.theme.id" :challengeId="challenge.id" :challengePageId="challenge.pages[challengePage].id" v-on:entry-created="loadJournalEntries()" />
                <b-btn v-if="challengePageJournalEntries.length === 0" id="add-new-journal" class="mt-3 mb-4" variant="light" v-b-modal.modal-create-entry >
                  <icon name="pen" style="font-size: 12px; opacity: 0.6; vertical-align: baseline;"/> {{ $t('journal:add-new') }}
                </b-btn>
                <h5 v-if="challengePageJournalEntries.length > 0">{{ $t('challenges:your-notes') }}</h5>
                <div class="challenge-page-journal-entry" v-for="entry in challengePageJournalEntries" :key="entry._id">
                  <div class="entry-created-date my-1">{{ entry.createdAt | moment('from') }}</div>
                  <p class="my-1">{{ entry.content }}</p>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-container>
        <b-container class="challenge-page h-100" fluid v-if="challengeEnded">
          <b-row align-h="center" align-v="center" class="h-100">
            <b-col cols="12" md="10" lg="8" xl="8" v-if="randomizedChallengeInsights && randomizedChallengeInsights.length > 0" class="pb-3">
              <h2 class="title">{{ $t('challenges:recap-title') }}</h2>
              <insight-gem v-for="insight in randomizedChallengeInsights" :key="insight.id" :insight="insight" v-on:select="selectGem" :selected="insight.id === selectedGem"></insight-gem>
            </b-col>
            <b-col cols="12" md="10" lg="8" xl="8" v-else class="text-center">
              <h2 class="title">{{ $t('challenges:finished-title', { challengeTitle: challenge.title }) }}</h2>
            </b-col>
          </b-row>
        </b-container>
      </div>
      <div class="overlay-footer">
        <b-container fluid>
          <b-row align-h="center">
            <b-col class="text-center mt-3 mb-3 my-md-4">
              <b-button class="float-right" id="startlearning" size="lg" variant="success" @click="startChallenge" v-if="!challengeStarted && !challengeEnded && (previouslyChallengePageIndex === null || previouslyChallengeFinished)">
                {{ $t('challenges:start') }}
              </b-button>
              <b-button size="lg" class="float-left mr-3" variant="light" v-show="challengePage > 0" @click.stop="changePageBackward" v-if="challengeStarted">
                {{ $t('challenges:previous') }}
              </b-button>
              <b-button size="lg" class="float-right" variant="success" @click="changePage" v-if="challengeStarted && !challengeEnded">
                {{ $t('challenges:next') }}
              </b-button>
              <b-button class="float-right" :disabled="selectedGem == null" @click="finishChallenge" size="lg" variant="success" v-if="challengeStarted && challengeEnded">
                {{ $t('challenges:finish') }}
              </b-button>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CreateJournalEntry from '../personal/journal/createJournalEntry/CreateJournalEntry'
import InsightGem from './InsightGem'

function shuffleArray (origArray) {
  const array = origArray.slice(0)
  for (var i = array.length - 1; i > 0; i--) {
    var j = Math.floor(Math.random() * (i + 1))
    var temp = array[i]
    array[i] = array[j]
    array[j] = temp
  }
  return array
}

export default {
  name: 'Challenge',
  components: { CreateJournalEntry, InsightGem },
  data () {
    return {
      challenge: null,
      challengeLength: null,
      challengePage: null,
      challengeStarted: false,
      challengeEnded: false,
      challengeFinished: false,
      previouslyChallengePageIndex: null,
      selectedGem: null
    }
  },
  created () {
    this.$emit('challenge-opened')
  },
  async mounted () {
    this.getChallengeProgress()
    await this.loadChallenge()
  },
  watch: {
    challengePage: {
      immediate: false,
      handler (newVal, oldVal) {
        this.loadJournalEntries()
      }
    },
    language: {
      immediate: true,
      async handler (newVal, oldVal) {
        await this.loadChallenge()
      }
    }
  },
  methods: {
    loadChallenge: function () {
      return new Promise((resolve, reject) => {
        this.axios.get('/private/content/challenges/' + this.$route.params.challengeId + '/?lang=' + this.$store.state.language).then((response) => {
          this.challenge = response.data.challenge
          this.challengeLength = this.challenge.pages.length
          resolve()
        }).catch(err => {
          reject(err)
        })
      })
    },
    loadJournalEntries: function () {
      if (this.challenge && this.challenge.pages) {
        this.$store.dispatch('loadChallengePageJournalEntries', {
          themeId: this.$route.params.themeId,
          challengeId: this.$route.params.challengeId,
          challengePageId: this.challenge.pages[this.challengePage].id
        })
      }
    },
    getChallengeProgress: function () {
      return new Promise((resolve, reject) => {
        this.axios.get(`/tracking/challenge/${this.$route.params.challengeId}`).then((response) => {
          if (response.data.progress.data.challengePageId) {
            this.previouslyChallengePageIndex = response.data.progress.data.challengePageIndex
            this.previouslyChallengeFinished = response.data.progress.data.challengeFinished
          }
          resolve()
        }).catch(err => {
          // Tracking info may not exist, currently gives 404
          resolve(err)
        })
      })
    },
    trackChallengeProgress: function () {
      this.axios.post(`/tracking/${this.$route.params.challengeId}`, {
        themeId: this.$route.params.themeId,
        challengeId: this.$route.params.challengeId,
        challengePageId: this.challenge.pages[this.challengePage].id,
        challengePageIndex: this.challengePage,
        challengeStarted: this.challengeStarted,
        challengeFinished: this.challengeFinished
      })
      // This is a cludge that needs changing in the next version
      this.$parent.challengeProgresses[this.$route.params.challengeId] = {
        challengePageIndex: this.challengePage,
        challengeStarted: this.challengeStarted,
        challengeFinished: this.challengeFinished
      }
    },
    startChallenge: function () {
      this.challengeStarted = true
      this.challengePage = 0
      this.trackChallengeProgress()
    },
    finishChallenge: function () {
      this.axios.post('/challenges/gem', { challengeInsightId: this.selectedGem, challengeId: this.challenge.id, themeId: this.$parent.theme.id })
        .then((response) => {})
      this.challengeFinished = true
      this.trackChallengeProgress()
      this.$router.push({ name: 'ChallengeTheme', params: { themeId: this.$parent.theme.id } })
    },
    changePageBackward: function () {
      if (this.challengeEnded) {
        this.challengeEnded = false
      } else if (this.challengePage >= 1) {
        this.challengePage -= 1
      }
      this.trackChallengeProgress()
    },
    changePage: function () {
      if (this.challengePage < this.challenge.pages.length - 1) {
        this.challengePage += 1
      } else {
        this.challengeEnded = true
      }
      this.trackChallengeProgress()
    },
    continueChallenge: function () {
      this.challengePage = this.previouslyChallengePageIndex
      this.challengeStarted = true
      this.previouslyChallengePageIndex = null
      this.trackChallengeProgress()
    },
    startOver: function () {
      this.challengeStarted = false
      this.previouslyChallengePageIndex = null
    },
    selectGem: function (insight) {
      this.selectedGem = insight.id
    }
  },
  computed: {
    ...mapGetters(['language', 'challengePageJournalEntries', 'account']),
    challengeProgress: function () {
      return this.challengeStarted ? this.challengePage + 1 : 0
    },
    randomizedChallengeInsights: function () {
      if (this.challenge && this.challenge.insights && this.challenge.insights.length > 0) {
        return shuffleArray(this.challenge.insights)
      }
      return []
    }
  }
}
</script>

<style lang="scss">
.challenge-page {
    padding: 0 2rem;
    @media (min-width: 576px) {
      font-size: 1rem;
    }
    @media (min-width: 768px) {
      font-size: 1.1rem;
    }
    @media (min-width: 992px) {
      font-size: 1.2rem;
    }
    @media (min-width: 1200px) {
      font-size: 1.3rem;
    }

  .title {
    font-size: 1.75em;
    font-weight: 300;

    margin-top: 1rem;
    margin-bottom: 1em;

    @media (min-width: 576px) {
      margin-top: 3rem;
      margin-bottom: 1em;
    }
    @media (min-width: 768px) {
    }
    @media (min-width: 992px) {
    }
    @media (min-width: 1200px) {
    }

  }
  .content {
    font-size: 1em;
    font-weight: normal;
    white-space: pre-line;
  }
  .challenge-page-journal-entries {
    border-top: 1px solid #efefef;
    margin-top: 1.5em;
    padding-top: 0.5em;
    h5 {
      font-weight: bold;
    }
    .challenge-page-journal-entry {
      font-size: 0.85em;
    }
  }
}

.challenge-progress {
  background-color: white;
}

.bounce-enter-active {
  animation: bounce-in .5s;
}
.bounce-leave-active {
  animation: bounce-in .5s reverse;
}
@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}
</style>
